import { useLocale } from "@/i18n";

/**
 * Compact IT | EN language switcher with inline SVG flags.
 * We use SVGs instead of emoji flags because Windows Chrome/Edge do not
 * render regional indicator emoji as flags.
 */

function FlagIT({ className = "" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 3 2"
      className={className}
      preserveAspectRatio="none"
    >
      <rect width="1" height="2" x="0" fill="#009246" />
      <rect width="1" height="2" x="1" fill="#F1F2F1" />
      <rect width="1" height="2" x="2" fill="#CE2B37" />
    </svg>
  );
}

function FlagGB({ className = "" }) {
  // Simplified Union Jack. 60x30 viewBox. At ~16x11px the subtle asymmetry
  // of the real flag is imperceptible, so we draw symmetric crosses.
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 60 30"
      className={className}
      preserveAspectRatio="none"
    >
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="3" />
      <path d="M30,0 V30 M0,15 H60" stroke="#FFFFFF" strokeWidth="10" />
      <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}

export default function LanguageSwitcher({ className = "" }) {
  const { locale, setLocale } = useLocale();
  const isIT = locale === "it";
  const baseBtn =
    "flex items-center gap-1.5 px-2 py-0.5 font-geist-mono text-[10px] uppercase tracking-wider rounded-sm transition-colors";
  const active = "bg-white/[0.08] text-white";
  const idle = "text-white/40 hover:text-white";
  const flagCls =
    "w-4 h-[11px] rounded-[1px] border border-white/15 shadow-[0_0_0_0.5px_rgba(0,0,0,0.4)]";
  return (
    <div
      role="group"
      aria-label="Language"
      data-testid="language-switcher"
      className={`inline-flex items-center gap-1 border border-white/[0.1] rounded-sm px-1 py-0.5 bg-white/[0.02] ${className}`}
    >
      <button
        type="button"
        onClick={() => setLocale("it")}
        aria-pressed={isIT}
        aria-label="Italiano"
        data-testid="language-switcher-it"
        className={`${baseBtn} ${isIT ? active : idle}`}
      >
        <FlagIT className={flagCls} />
        <span>IT</span>
      </button>
      <span className="text-white/15" aria-hidden="true">
        |
      </span>
      <button
        type="button"
        onClick={() => setLocale("en")}
        aria-pressed={!isIT}
        aria-label="English"
        data-testid="language-switcher-en"
        className={`${baseBtn} ${!isIT ? active : idle}`}
      >
        <FlagGB className={flagCls} />
        <span>EN</span>
      </button>
    </div>
  );
}
